import { action, computed, observable } from 'mobx';
import ReactPlayer from 'react-player';
import { IVideoBlockResponse } from '~/types/server.types';
import { BaseBlock } from '~/models/blocks/base-block.model';
import { IVideoBlockStyles } from '~/types/block-styles.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';
import { StreamingPlatform } from '~/components/Builder/Block/VideoBlock/VideoBlock.types';
import { detectStreamingPlatformFromUrl } from '~/common/VideoBlock.utils';
import { IFRAME_VIDEO_MAP } from '~/common/VideoPlayer/VideoPlayer';

type VideoBlockDataForServer = ReturnType<BaseBlock['getDataForServer']> &
    Pick<IVideoBlockResponse, 'type'>;

export class VideoBlock extends BaseBlock {
    type: 'video_block';
    @observable styles: IVideoBlockStyles;
    @observable hasPlayerError: boolean = false;

    constructor(
        block: IVideoBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.styles = block.styles;
    }

    get isDeletable() {
        return true;
    }

    get isPlaying() {
        return undefined;
    }

    get isAutoplayOn() {
        return false;
    }

    get isMuted() {
        return undefined;
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    @computed
    get isValidVideo() {
        if (this.hasPlayerError) {
            return false;
        }
        if (!!IFRAME_VIDEO_MAP[this.streamingPlatform]) {
            return true;
        }
        if (!!this.streamingPlatform) {
            return ReactPlayer.canPlay(this.videoLink);
        }
        return false;
    }

    @computed
    get videoLink() {
        return this.styles?.video_link.trim();
    }

    @computed
    get isNoVideo() {
        return !this.videoLink;
    }

    @computed
    get isShowPlaceholder() {
        return this.isNoVideo || this.hasError;
    }

    @computed
    get isVideoControlsOn() {
        return this.styles.video_controls;
    }

    @computed
    get isTransparentBackgroundOn() {
        if (!this.isPicTimeVideo) {
            return false;
        }
        return this.styles.is_transparent_background;
    }

    @computed
    get isLoopOn() {
        return this.styles.loop_forever;
    }

    @computed
    get streamingPlatform(): StreamingPlatform {
        if (!this.videoLink) {
            return null;
        }
        const url = this.videoLink;
        return detectStreamingPlatformFromUrl(url);
    }

    @computed
    get isLoomVideo() {
        return this.streamingPlatform === StreamingPlatform.Loom;
    }

    @computed
    get isPicTimeVideo() {
        return this.streamingPlatform === StreamingPlatform.PicTime;
    }

    @computed
    get isAllowControlToggle() {
        return !this.isLoomVideo;
    }

    @computed
    get isAllowLoopToggle() {
        return !this.isLoomVideo;
    }

    @computed
    get isShowToggles() {
        return !this.isNoVideo;
    }

    @computed
    get loaderColor() {
        return this.rootStore.companyStore?.currCompany?.brand_color;
    }

    @computed
    get errors() {
        const {
            builderUiStore: { showErrors }
        } = this.rootStore;

        const errors: any = {};

        if (!showErrors) {
            return errors;
        }

        if (this.isLoomVideo) {
            return errors;
        }

        if (this.isNoVideo || !this.isValidVideo) {
            errors.hasErrors = true;
        }
        return errors;
    }

    @computed
    get hasError() {
        return this.hasPlayerError || this.errors?.hasErrors;
    }

    @computed
    get parentWidth() {
        const horizontalParent = this.calculateRootLevelParent();
        let verticalParent = horizontalParent.children.find(
            ({ block_id }) => block_id === this.parent_id
        );

        if (!verticalParent) {
            verticalParent = this.parent.parent.children.find(
                ({ block_id }) => block_id === this.parent_id
            );
        }

        return verticalParent.size === 1
            ? 100 / horizontalParent.children.length
            : verticalParent.size;
    }

    @action.bound
    setHasPlayerError(value: boolean) {
        this.hasPlayerError = value;
    }

    getDataForServer(): VideoBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'video_block'
        };
    }
}
